import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  CircularProgress,
  makeStyles,
  Switch,
  Typography,
  withStyles
} from '@material-ui/core';

const BRAND = '#D9042F';

const itemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool
});

export const BrandSwitch = withStyles({
  switchBase: {
    '&$checked': { color: BRAND },
    '&$checked + $track': { backgroundColor: BRAND, opacity: 0.5 }
  },
  checked: {},
  track: {}
})(Switch);


function ConfigSwitchSection({
  title,
  items,
  values,
  savingKeys,
  disabled,
  onToggle
}) {
  const classes = useStyles();

  return (
    <Box className={classes.card}>
      {title && (
        <Box className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
        </Box>
      )}
      <Box className={classes.body}>
        {items.map(item => {
          const saving = savingKeys.includes(item.key);
          const checked = values?.[item.key] === true;
          const locked = disabled || saving || item.disabled;
          return (
            <Box
              key={item.key}
              component="label"
              className={clsx(classes.row, locked && classes.rowLocked)}
            >
              <Box flex={1} minWidth={0} pr={1}>
                <Typography className={classes.label}>{item.label}</Typography>
              </Box>
              {saving && <CircularProgress size={16} className={classes.spinner} />}
              <BrandSwitch
                checked={checked}
                disabled={locked}
                onChange={e => onToggle(item.key, e.target.checked)}
                inputProps={{ 'aria-label': item.label }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

ConfigSwitchSection.propTypes = {
  title: PropTypes.node,
  items: PropTypes.arrayOf(itemShape).isRequired,
  values: PropTypes.object,
  savingKeys: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
};

ConfigSwitchSection.defaultProps = {
  values: {},
  savingKeys: [],
  disabled: false
};

export function ConfigSwitchPanel({ sections, minColumnWidth, ...rest }) {
  return (
    <Box
      display="grid"
      gridGap={16}
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`
      }}
    >
      {sections.map(section => (
        <ConfigSwitchSection
          key={section.id}
          title={section.title}
          items={section.items}
          {...rest}
        />
      ))}
    </Box>
  );
}

ConfigSwitchPanel.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.node,
          items: PropTypes.arrayOf(itemShape).isRequired
    })
  ).isRequired,
  minColumnWidth: PropTypes.number
};

ConfigSwitchPanel.defaultProps = {
  minColumnWidth: 240
};

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e8eaed',
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    overflow: 'hidden',
    transition: 'box-shadow .2s, border-color .2s',
    '&:hover': {
      borderColor: '#dcdfe4',
      boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid #f0f1f3'
  },
  title: {
    flex: 1,
    fontSize: 15,
    fontWeight: 600,
    color: '#1f2329'
  },
  badge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 999,
    color: '#6b7280',
    backgroundColor: '#f2f3f5',
    whiteSpace: 'nowrap'
  },
  badgeActive: {
    color: '#0f8a4a',
    backgroundColor: '#e7f6ee'
  },
  body: {
    padding: 6
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 4px 8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color .15s',
    '&:hover': {
      backgroundColor: '#f7f8fa'
    }
  },
  rowLocked: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1f2329',
    lineHeight: 1.4
  },
  status: {
    fontSize: 12,
    color: '#9aa0a6',
    marginTop: 2
  },
  statusOn: {
    color: '#0f8a4a'
  },
  spinner: {
    color: BRAND,
    marginRight: 4
  }
}));

export default ConfigSwitchSection;
